import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { trialTodaySentence } from "../i18n/quotaCopy";
import { Sidebar } from "./QueryPage";
import ClinicalThemeToggle from "../components/ClinicalThemeToggle";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  User,
  Building2,
  Key,
  RefreshCw,
  Copy,
  Check,
  CreditCard,
  Loader2,
  Stethoscope,
  Save,
} from "lucide-react";
import { format } from "date-fns";
import { useEntitlements } from "../hooks/useEntitlements";
import PaywallModal from "../components/PaywallModal";
import HealthProfileExtendedForm from "../components/HealthProfileExtendedForm";
import { FEATURE_PRIVACY_POLICY } from "../config/featureFlags";
import { LEGAL_PRIVACY_URL } from "../config/legalUrls";
import { Link } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { HEALTH_PROFILE_ONBOARDING_PATH } from "../routing/b2cAccess";
import { currentRegion } from "../config/regions";

const AccountPage = () => {
  const { user, logout, fetchBackendUser } = useAuth();
  const { t, language } = useLanguage();
  const { entitlements } = useEntitlements();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [clinicProfile, setClinicProfile] = useState({ clinic_name: "", doctor_name: "" });
  const [savingClinic, setSavingClinic] = useState(false);
  const [healthProfile, setHealthProfile] = useState(null);
  const [tierA, setTierA] = useState({
    birth_year: "",
    sex_at_birth: "",
    gender_identity: "",
    pregnancy_status: "not_applicable",
  });
  const [savingTierA, setSavingTierA] = useState(false);
  const [clearingProfile, setClearingProfile] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const isCis = currentRegion === "cis";

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const response = await axios.get(`${API}/account`);
        setAccount(response.data?.data);
      } catch (error) {
        toast.error(t("account.loadError"));
      } finally {
        setLoading(false);
      }
    };
    const fetchClinicProfile = async () => {
      try {
        const resp = await axios.get(`${API}/settings/clinic-profile`);
        if (resp.data?.data) setClinicProfile(resp.data.data);
      } catch {}
    };
    const fetchHealth = async () => {
      try {
        const resp = await axios.get(`${API}/profile/health`);
        const data = resp.data?.data;
        if (!data) return;
        setHealthProfile(data);
        const ta = data.health_profile?.tier_a || {};
        setTierA({
          birth_year: ta.birth_year != null ? String(ta.birth_year) : "",
          sex_at_birth: ta.sex_at_birth || "",
          gender_identity: ta.gender_identity || "",
          pregnancy_status: ta.pregnancy_status || "not_applicable",
        });
      } catch {}
    };
    fetchAccount();
    fetchClinicProfile();
    fetchHealth();
  }, []);

  const handleRegenerateKey = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/account/api-key/regenerate`, {});
      setAccount((a) => ({ ...a, api_key: response.data?.data?.api_key }));
      toast.success(t("account.keyRegenerated"));
    } catch (error) {
      toast.error(t("account.keyRegenerateError"));
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(account?.api_key || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(t("account.keyCopied"));
  };

  const handleSaveClinicProfile = async () => {
    setSavingClinic(true);
    try {
      await axios.put(`${API}/settings/clinic-profile`, clinicProfile);
      toast.success(t("account.clinicSaved"));
    } catch {
      toast.error(t("account.clinicSaveError"));
    } finally {
      setSavingClinic(false);
    }
  };

  const handleSaveTierA = async () => {
    const year = Number.parseInt(tierA.birth_year, 10);
    if (!Number.isFinite(year) || !tierA.sex_at_birth) {
      toast.error(t("healthProfile.tierARequired"));
      return;
    }
    setSavingTierA(true);
    try {
      const res = await axios.put(`${API}/profile/health/tier-a`, {
        birth_year: year,
        sex_at_birth: tierA.sex_at_birth,
        gender_identity: tierA.gender_identity.trim() || null,
        pregnancy_status: tierA.pregnancy_status || "not_applicable",
      });
      setHealthProfile((hp) => ({
        ...(hp || {}),
        tier_a_complete: res.data?.data?.tier_a_complete,
        tier_extended_complete: res.data?.data?.tier_extended_complete,
        completion_score: res.data?.data?.completion_score,
      }));
      await fetchBackendUser?.();
      toast.success(t("healthProfile.tierASaved"));
    } catch {
      toast.error(t("healthProfile.saveError"));
    } finally {
      setSavingTierA(false);
    }
  };

  const handleClearHealthProfile = async () => {
    setClearingProfile(true);
    try {
      await axios.delete(`${API}/profile/health`);
      setHealthProfile({
        tier_a_complete: false,
        tier_extended_complete: false,
        completion_score: 0,
        health_profile: null,
      });
      setTierA({
        birth_year: "",
        sex_at_birth: "",
        gender_identity: "",
        pregnancy_status: "not_applicable",
      });
      await fetchBackendUser?.();
      toast.success(t("healthProfile.cleared"));
    } catch {
      toast.error(t("healthProfile.saveError"));
    } finally {
      setClearingProfile(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (isCis && !deletePassword.trim()) {
      toast.error(t("account.deleteAccountPassword"));
      return;
    }
    setDeletingAccount(true);
    try {
      await axios.delete(`${API}/auth/me`, {
        data: isCis ? { password: deletePassword } : {},
      });
      toast.success(t("account.deleteAccountSuccess"));
      setDeleteOpen(false);
      await logout();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 && !isCis) {
        toast.error(t("account.deleteAccountReauth"));
      } else {
        toast.error(
          err?.response?.data?.detail || t("account.deleteAccountError"),
        );
      }
    } finally {
      setDeletingAccount(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center pl-14 md:pl-0">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      </div>
    );
  }

  const org = account?.organization;
  const apiKey = account?.api_key;

  return (
    <div className="flex h-screen bg-background" data-testid="account-page">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 md:h-16 border-b border-border flex items-center px-4 md:px-6 pl-14 md:pl-6">
          <div className="flex items-center gap-2 md:gap-3">
            <User className="w-4 h-4 md:w-5 md:h-5 text-muted-foreground" />
            <h1 className="text-base md:text-lg font-semibold">{t("account.title")}</h1>
          </div>
        </header>

        <ScrollArea className="flex-1">
          <div className="max-w-2xl mx-auto p-3 md:p-6 space-y-4 md:space-y-6">
            {/* Profile Card */}
            <Card>
              <CardHeader className="px-4 md:px-6">
                <CardTitle className="text-base">{t("account.profile")}</CardTitle>
              </CardHeader>
              <CardContent className="px-4 md:px-6">
                <div className="flex items-center gap-3 md:gap-4">
                  <Avatar className="w-12 h-12 md:w-16 md:h-16">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-lg md:text-xl">{user?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base md:text-lg truncate">{user?.name}</h3>
                    <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
                    <Badge variant="secondary" className="mt-1.5 md:mt-2 text-xs">
                      {user?.role}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="px-4 md:px-6">
                <CardTitle className="text-base">{t("theme.clinicalAppearance")}</CardTitle>
                <CardDescription>{t("theme.clinicalAppearanceDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="px-4 md:px-6">
                <ClinicalThemeToggle />
              </CardContent>
            </Card>

            <Card data-testid="account-health-profile">
              <CardHeader className="px-4 md:px-6">
                <CardTitle className="text-base">{t("healthProfile.accountTitle")}</CardTitle>
                <CardDescription>{t("healthProfile.accountDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="px-4 md:px-6 space-y-5">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium">{t("healthProfile.tierATitle")}</h4>
                  {!healthProfile?.tier_a_complete && (
                    <p className="text-xs text-muted-foreground">
                      <Link
                        to={HEALTH_PROFILE_ONBOARDING_PATH}
                        className="underline underline-offset-2"
                      >
                        {t("healthProfile.completeTierALink")}
                      </Link>
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>{t("healthProfile.birthYear")}</Label>
                      <Input
                        type="number"
                        value={tierA.birth_year}
                        onChange={(e) =>
                          setTierA((s) => ({ ...s, birth_year: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("healthProfile.sexAtBirth")}</Label>
                      <Select
                        value={tierA.sex_at_birth}
                        onValueChange={(v) =>
                          setTierA((s) => ({ ...s, sex_at_birth: v }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("healthProfile.selectPlaceholder")} />
                        </SelectTrigger>
                        <SelectContent>
                          {["female", "male", "intersex", "prefer_not_to_say"].map((opt) => (
                            <SelectItem key={opt} value={opt}>
                              {t(`healthProfile.sex.${opt}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("healthProfile.genderIdentity")}</Label>
                    <Input
                      value={tierA.gender_identity}
                      onChange={(e) =>
                        setTierA((s) => ({ ...s, gender_identity: e.target.value }))
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleSaveTierA}
                    disabled={savingTierA}
                    data-testid="account-save-tier-a"
                  >
                    {savingTierA ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t("healthProfile.saveTierA")
                    )}
                  </Button>
                </div>

                <Separator />

                <div className="space-y-3">
                  <h4 className="text-sm font-medium">{t("healthProfile.extendedTitle")}</h4>
                  <HealthProfileExtendedForm
                    initialExtended={healthProfile?.health_profile?.tier_extended || {}}
                    subjectKind="self"
                    submitLabel={t("healthProfile.saveExtended")}
                    onSaved={async (data) => {
                      setHealthProfile((hp) => ({
                        ...(hp || {}),
                        tier_a_complete: data?.tier_a_complete ?? hp?.tier_a_complete,
                        tier_extended_complete:
                          data?.tier_extended_complete ?? hp?.tier_extended_complete,
                        completion_score: data?.completion_score,
                      }));
                      await fetchBackendUser?.();
                    }}
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t("healthProfile.deleteDataNote")}{" "}
                    {FEATURE_PRIVACY_POLICY ? (
                      <a
                        href={LEGAL_PRIVACY_URL}
                        className="underline underline-offset-2"
                        rel="noopener noreferrer"
                        data-testid="account-privacy-link"
                      >
                        {t("healthProfile.privacyLink")}
                      </a>
                    ) : null}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClearHealthProfile}
                    disabled={clearingProfile}
                    data-testid="account-clear-health-profile"
                  >
                    {clearingProfile ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t("healthProfile.clearProfile")
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Organization Card */}
            {org && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <CardTitle className="text-base">{t("account.organization")}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{org.name}</p>
                      <p className="text-sm text-muted-foreground">council.app/{org.slug}</p>
                    </div>
                    <Badge>{org.plan} {t("account.planSuffix")}</Badge>
                  </div>
                  <Separator />
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">{t("account.createdLabel")}</p>
                      <p className="font-medium">
                        {org.created_at ? format(new Date(org.created_at), "MMM d, yyyy") : "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">{t("account.orgId")}</p>
                      <p className="font-mono text-xs">{org.org_id}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Clinic Profile (White-label) — org users only */}
            {user?.org_id && (
            <Card>
              <CardHeader className="px-4 md:px-6">
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4" />
                  <CardTitle className="text-base">{t("account.clinicProfile")}</CardTitle>
                </div>
                <CardDescription>{t("account.clinicProfileDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="px-4 md:px-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("account.clinicName")}</label>
                  <Input
                    data-testid="clinic-name-input"
                    placeholder={t("account.clinicNamePlaceholder")}
                    value={clinicProfile.clinic_name}
                    onChange={(e) => setClinicProfile(p => ({ ...p, clinic_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t("account.doctorName")}</label>
                  <Input
                    data-testid="doctor-name-input"
                    placeholder={t("account.doctorNamePlaceholder")}
                    value={clinicProfile.doctor_name}
                    onChange={(e) => setClinicProfile(p => ({ ...p, doctor_name: e.target.value }))}
                  />
                </div>
                <Button
                  data-testid="save-clinic-btn"
                  onClick={handleSaveClinicProfile}
                  disabled={savingClinic}
                  size="sm"
                  className="gap-2"
                >
                  {savingClinic ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {t("account.save")}
                </Button>
              </CardContent>
            </Card>
            )}

            {/* Billing */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  <CardTitle className="text-base">{t("account.billing")}</CardTitle>
                </div>
                <CardDescription>{t("account.billingDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {entitlements ? (
                  <div className="rounded-sm border border-border p-4 space-y-2 text-sm">
                    {entitlements.trial_active ? (
                      <p data-testid="account-trial-summary">
                        {trialTodaySentence(
                          t,
                          language,
                          entitlements.trial_daily_cases_remaining ?? 0,
                        )}
                        {" · "}
                        {entitlements.trial_ends_at
                          ? new Date(entitlements.trial_ends_at).toLocaleDateString()
                          : "—"}
                      </p>
                    ) : null}
                    {(entitlements.onboarding_bonus_cases_remaining ?? 0) > 0 ? (
                      <p data-testid="account-bonus-remaining">
                        {t("billing.onboardingBonusRemaining").replace(
                          "{count}",
                          String(entitlements.onboarding_bonus_cases_remaining),
                        )}
                      </p>
                    ) : !entitlements.onboarding_bonus_granted &&
                      (entitlements.trial_active || entitlements.plan === "free") ? (
                      <p className="text-muted-foreground" data-testid="account-bonus-cta">
                        {t("billing.onboardingBonusCta")}
                      </p>
                    ) : null}
                    <p data-testid="account-today-usage">{t("billing.accountBalance")
                      .replace("{free}", String(entitlements.free_cases_remaining ?? 0))
                      .replace("{paid}", String(entitlements.paid_cases_remaining ?? 0))
                      .replace("{sub}", String(entitlements.subscription_cases_remaining ?? 0))}</p>
                    <p className="text-muted-foreground capitalize" data-testid="account-plan">
                      {t("billing.currentPlan")}: {entitlements.plan}
                    </p>
                    {!entitlements.trial_active && entitlements.can_submit === false ? (
                      <p className="text-muted-foreground">{t("billing.postTrialPaywallHint")}</p>
                    ) : null}
                  </div>
                ) : null}
                <div className="flex flex-col items-start gap-1">
                  <Button
                    type="button"
                    data-testid="account-get-more-cases"
                    onClick={() => setPaywallOpen(true)}
                  >
                    {t("billing.getMoreCases")}
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto px-0"
                    data-testid="account-view-plans"
                    onClick={() => setPaywallOpen(true)}
                  >
                    {t("billing.viewPlans")}
                  </Button>
                </div>
                {!entitlements?.checkout_enabled && (
                  <p className="text-xs text-muted-foreground">{t("billing.checkoutUnavailable")}</p>
                )}
              </CardContent>
            </Card>

            {/* API Key Card — org / developer users only */}
            {user?.org_id && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  <CardTitle className="text-base">{t("account.apiKey")}</CardTitle>
                </div>
                <CardDescription>{t("account.apiKeyDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    value={apiKey || ""}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button variant="outline" size="icon" onClick={handleCopyKey}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t("account.regenerate")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t("account.regenerateConfirmTitle")}</DialogTitle>
                      <DialogDescription>
                        {t("account.regenerateConfirmDesc")}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline">{t("account.cancel")}</Button>
                      <Button onClick={handleRegenerateKey} disabled={regenerating}>
                        {regenerating ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            {t("account.regenerating")}
                          </>
                        ) : (
                          t("account.regenerateShort")
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <div className="text-xs text-muted-foreground space-y-1">
                  <p>{t("account.exampleUsage")}</p>
                  <code className="block p-2 bg-muted rounded-sm font-mono text-xs overflow-x-auto">
                    {`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/council/query" \\
  -H "Authorization: Bearer ${apiKey?.substring(0, 20)}..." \\
  -H "Content-Type: application/json" \\
  -d '{"query_text": "Your question here"}'`}
                  </code>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Logout */}
            <Button variant="outline" onClick={logout} className="w-full" data-testid="logout-btn">
              {t("account.signOut")}
            </Button>

            <Dialog
              open={deleteOpen}
              onOpenChange={(open) => {
                setDeleteOpen(open);
                if (!open) setDeletePassword("");
              }}
            >
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full text-destructive border-destructive/40 hover:bg-destructive/5"
                  data-testid="account-delete-open"
                >
                  {t("account.deleteAccount")}
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="account-delete-dialog">
                <DialogHeader>
                  <DialogTitle>{t("account.deleteAccountTitle")}</DialogTitle>
                  <DialogDescription>
                    {t("account.deleteAccountDesc")}
                  </DialogDescription>
                </DialogHeader>
                {isCis ? (
                  <div className="space-y-2">
                    <Label htmlFor="delete-account-password">
                      {t("account.deleteAccountPassword")}
                    </Label>
                    <Input
                      id="delete-account-password"
                      type="password"
                      autoComplete="current-password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder={t("account.deleteAccountPasswordPlaceholder")}
                      data-testid="account-delete-password"
                    />
                  </div>
                ) : null}
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDeleteOpen(false)}
                  >
                    {t("account.cancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={handleDeleteAccount}
                    disabled={deletingAccount}
                    data-testid="account-delete-confirm"
                  >
                    {deletingAccount ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t("account.deleteAccountConfirm")
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </ScrollArea>
      </div>
      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        checkoutEnabled={Boolean(entitlements?.checkout_enabled)}
        onDismiss={() => setPaywallOpen(false)}
      />
    </div>
  );
};

export default AccountPage;
